import React from 'react';
import { Row, Col, Select, Input, Button, Switch, Form, Space, Avatar } from 'antd';
import { RedoOutlined } from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import { CONTRACTORS } from '../../graphql/queries/contractors';
import { IContractor } from '../../types/contractor';
import { SIZES_RANGE } from '../../consts/sizes';
import { IBaseFilter } from '../../types/plans';

const { Option } = Select;

interface AdminPlanFiltersProps {
  filters: IBaseFilter;
  setFilters: (filters: IBaseFilter) => void;
  role?: string;
  setUnlocked?: (unlocked: boolean) => void;
}

const AdminPlanFilters: React.FC<AdminPlanFiltersProps> = ({
  filters,
  setFilters,
  role = 'USER',
  setUnlocked = null,
}) => {
  const { data, refetch } = useQuery<{ contractors: IContractor[] }>(CONTRACTORS);
  const contractors = (data && data.contractors) || [];

  const handleChange = (value: string | number | object | boolean, field: string) => {
    if (setUnlocked) setUnlocked(false);
    if (typeof value === 'object' && value !== null) {
      if (!filters[field]) filters[field] = {};
      const fieldValue = typeof filters[field] === 'object' ? (filters[field] as object) : {};
      setFilters({
        ...filters,
        [field]: { ...fieldValue, ...(value as object) },
      });
      return;
    }
    setFilters({ ...filters, [field]: value });
  };

  const handleChangeSize = (
    e: React.ChangeEvent<HTMLInputElement>,
    sizeField: string,
    valueField: string
  ) => {
    if (setUnlocked) setUnlocked(false);
    const newState = { ...filters };
    if (!newState.meta) newState.meta = {};
    if (!newState.meta[sizeField]) {
      newState.meta = {
        ...newState.meta,
        [sizeField]: {
          [valueField]: +e.target.value,
        },
      };
      setFilters(newState);
      return;
    }

    newState.meta[sizeField] = {
      ...(newState.meta[sizeField] as object),
      [valueField]: +e.target.value,
    };
    setFilters(newState);
  };

  const onDropFilter = () => {
    setFilters({ contractorId: '' });
    refetch();
  };

  const getValueArea = () => {
    if (!filters.meta || !filters.meta.outerArea) return null;
    const key = `${filters.meta.outerArea.gte}${filters.meta.outerArea.lte ? ' - ' + filters.meta.outerArea.lte : '+'}`;
    return key;
  };

  return (
    <Form layout="horizontal" style={{ width: '100%' }}>
      <Row gutter={[16, 0]}>
        {role === 'ADMIN' && (
          <Col span={24} lg={5}>
            <Form.Item>
              <Select
                placeholder="Any contractor"
                value={filters.contractorId}
                style={{ width: '100%' }}
                onChange={value => handleChange(value, 'contractorId')}>
                <Option key="any" value="">
                  Any contractor
                </Option>
                {contractors.map((contractor: IContractor) => (
                  <Option key={contractor.id} value={contractor.id}>
                    <Avatar style={{ marginRight: 5 }} src={contractor.logo} />
                    {contractor.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        )}
        {role === 'ADMIN' && (
          <Col span={24} lg={3}>
            <Form.Item>
              <Select
                placeholder="Type"
                value={filters.meta?.type}
                style={{ width: '100%' }}
                onChange={value => handleChange({ type: value }, 'meta')}>
                <Option key="all" value="">
                  All
                </Option>
                <Option key="house" value="house">
                  House
                </Option>
                <Option key="shed" value="shed">
                  Shed
                </Option>
              </Select>
            </Form.Item>
          </Col>
        )}
        <Col span={24} lg={role === 'ADMIN' ? 4 : 8}>
          <Form.Item>
            <Select
              placeholder="Sq Ft"
              value={getValueArea()}
              style={{ width: '100%' }}
              onChange={value => handleChange({ outerArea: SIZES_RANGE[value] }, 'meta')}>
              <Option key="all" value="">
                All
              </Option>
              {Object.entries(SIZES_RANGE).map(([key]) => (
                <Option key={key} value={key}>
                  {key}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={24} lg={role === 'ADMIN' ? 6 : 8}>
          <Form.Item>
            <Select
              placeholder="Bedrooms"
              value={filters.meta?.bedroomCount}
              style={{ width: '100%' }}
              onChange={value => handleChange({ bedroomCount: value }, 'meta')}>
              <Option key="0" value={0}>
                Studio
              </Option>
              <Option key="1" value={1}>
                1
              </Option>
              <Option key="2" value={2}>
                2
              </Option>
              <Option key="3" value={3}>
                3
              </Option>
              <Option key="4" value={4}>
                4
              </Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={24} lg={role === 'ADMIN' ? 6 : 8}>
          <Form.Item>
            <Select
              placeholder="Bathrooms"
              value={filters.meta?.bathroomCount}
              style={{ width: '100%' }}
              onChange={value => handleChange({ bathroomCount: value }, 'meta')}>
              <Option key="bathroomCount-0" value={0}>
                0
              </Option>
              <Option key="bathroomCount-1" value={1}>
                1
              </Option>
              <Option key="bathroomCount-2" value={2}>
                2
              </Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[16, 0]} align="bottom" justify="space-between">
        <Col span={24} lg={16}>
          <Row gutter={[16, 0]}>
            <Col span={24} lg={12}>
              <Form.Item label="Width, ft">
                <Space.Compact>
                  <Input
                    addonBefore="from"
                    type="number"
                    min={0}
                    value={filters.meta?.width?.gte}
                    placeholder="0"
                    onChange={e => handleChangeSize(e, 'width', 'gte')}
                  />
                  <Input
                    addonBefore="to"
                    type="number"
                    min={0}
                    value={filters.meta?.width?.lte}
                    placeholder="100"
                    onChange={e => handleChangeSize(e, 'width', 'lte')}
                  />
                </Space.Compact>
              </Form.Item>
            </Col>
            <Col span={24} lg={12}>
              <Form.Item label="Length, ft">
                <Space.Compact>
                  <Input
                    addonBefore="from"
                    type="number"
                    min={0}
                    value={filters.meta?.length?.gte}
                    placeholder="0"
                    onChange={e => handleChangeSize(e, 'length', 'gte')}
                  />
                  <Input
                    addonBefore="to"
                    type="number"
                    min={0}
                    value={filters.meta?.length?.lte}
                    placeholder="100"
                    onChange={e => handleChangeSize(e, 'length', 'lte')}
                  />
                </Space.Compact>
              </Form.Item>
            </Col>
          </Row>
        </Col>
        <Col span={24} lg={8}>
          <Row gutter={[16, 0]} justify="space-between">
            {role === 'ADMIN' && (
              <Col span={24} lg={12}>
                <Form.Item label="Hidden">
                  <Switch
                    checked={filters.isHidden}
                    onChange={() => handleChange(!filters.isHidden, 'isHidden')}
                  />
                </Form.Item>
              </Col>
            )}
            <Col span={24} lg={12}>
              <Form.Item>
                <Button style={{ width: '100%' }} type="dashed" onClick={onDropFilter}>
                  <RedoOutlined />
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Col>
        {role === 'ADMIN' && (
          <Col span={24} lg={4}>
            <Form.Item label="Pre Approved">
              <Switch
                checked={filters.preApproved}
                onChange={() => handleChange(!filters.preApproved, 'preApproved')}
              />
            </Form.Item>
          </Col>
        )}
      </Row>
    </Form>
  );
};

export default AdminPlanFilters;
